import { ArrowRight, RotateCcw, TrendingUp } from 'lucide-react';
import type { KnowledgePointProgressView } from '@unity-learning/shared';
import {
  acquiredNotice,
  FOLLOW_UP_LABELS,
  knowledgeTarget,
  progressLine,
  stepClosedNote,
  type FollowUpAction,
} from '../utils/followUpActions';

const BUTTON_BASE =
  'inline-flex items-center max-w-full px-4 py-2 text-left text-sm font-medium rounded-md focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors';

const SPINNER = <div className="w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin flex-shrink-0"></div>;

interface FollowUpButtonsProps {
  /** Où en est l'élève sur le point de l'exercice ; `null` pour un exercice sans point du parcours (pas de troisième bouton). */
  progress: KnowledgePointProgressView | null;
  /** Le bouton dont la génération est en cours. */
  pending: FollowUpAction | null;
  /** Désactive les trois boutons (une génération ou une soumission est en cours). */
  disabled?: boolean;
  onAction: (action: FollowUpAction) => void;
  className?: string;
}

/**
 * Les boutons d'après-exercice : « Même niveau, autre situation », « Augmenter la difficulté », et, sur un
 * exercice du parcours, « Connaissance suivante : <titre> » (ou « Étape suivante : <titre> » après le dernier
 * point). Ils passent à la ligne quand la colonne se réduit (coach ouvert) ; un libellé long passe à la ligne
 * dans son bouton au lieu de déborder.
 */
export function FollowUpButtons({ progress, pending, disabled = false, onAction, className = '' }: FollowUpButtonsProps) {
  const target = knowledgeTarget(progress);
  const closedNote = stepClosedNote(progress);
  const acquired = Boolean(progress?.acquired);
  const busy = disabled || pending !== null;

  return (
    <div className={`flex flex-wrap gap-2 ${className}`} data-testid="follow-up-buttons">
      <button
        type="button"
        onClick={() => onAction('continue')}
        disabled={busy}
        data-action="continue"
        className={`${BUTTON_BASE} bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500`}
      >
        {pending === 'continue' ? (
          <>
            {SPINNER}
            Génération...
          </>
        ) : (
          <>
            <RotateCcw className="w-4 h-4 mr-1 flex-shrink-0" />
            {FOLLOW_UP_LABELS.continue}
          </>
        )}
      </button>
      <button
        type="button"
        onClick={() => onAction('next')}
        disabled={busy}
        data-action="next"
        className={`${BUTTON_BASE} bg-green-600 text-white hover:bg-green-700 focus:ring-green-500`}
      >
        {pending === 'next' ? (
          <>
            {SPINNER}
            Génération...
          </>
        ) : (
          <>
            <TrendingUp className="w-4 h-4 mr-1 flex-shrink-0" />
            {FOLLOW_UP_LABELS.next}
          </>
        )}
      </button>
      {target && (
        <button
          type="button"
          onClick={() => onAction('knowledge')}
          disabled={busy}
          data-action="knowledge"
          data-emphasis={acquired ? 'true' : 'false'}
          title={target.label}
          className={
            acquired
              ? `${BUTTON_BASE} bg-indigo-600 text-white hover:bg-indigo-700 focus:ring-indigo-500 ring-2 ring-indigo-300 dark:ring-indigo-400 ring-offset-1 dark:ring-offset-gray-900`
              : `${BUTTON_BASE} border border-indigo-300 dark:border-indigo-600 text-indigo-700 dark:text-indigo-300 bg-white dark:bg-transparent hover:bg-indigo-50 dark:hover:bg-indigo-900/30 focus:ring-indigo-500`
          }
        >
          {pending === 'knowledge' ? (
            <>
              {SPINNER}
              Génération...
            </>
          ) : (
            <>
              <ArrowRight className="w-4 h-4 mr-1 flex-shrink-0" />
              <span className="min-w-0 break-words">{target.label}</span>
            </>
          )}
        </button>
      )}
      {closedNote && (
        <p className="w-full text-sm text-gray-600 dark:text-gray-400" data-testid="step-closed-note">
          {closedNote}
        </p>
      )}
    </div>
  );
}

/** Le repère en haut d'un exercice du parcours : étape, rang de la connaissance, réussites, exercices faits. */
export function KnowledgeProgressLine({ progress, className = '' }: { progress: KnowledgePointProgressView; className?: string }) {
  return (
    <div
      className={`flex flex-wrap items-center gap-x-2 gap-y-1 rounded-lg border border-indigo-200 dark:border-indigo-800 bg-indigo-50 dark:bg-indigo-900/20 px-3 py-2 text-sm text-indigo-900 dark:text-indigo-200 ${className}`}
      data-testid="knowledge-progress"
    >
      <span className="min-w-0 break-words">{progressLine(progress)}</span>
      {progress.acquired && (
        <span className="rounded-full bg-green-100 dark:bg-green-900/40 px-2 py-0.5 text-xs font-medium text-green-800 dark:text-green-300">
          ✅ Acquise
        </span>
      )}
    </div>
  );
}

/** L'annonce dans la correction, à la réussite qui atteint le seuil d'acquisition. */
export function KnowledgeAcquiredNotice({ progress, className = '' }: { progress: KnowledgePointProgressView; className?: string }) {
  return (
    <div
      role="status"
      className={`rounded-lg border border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-900/20 px-4 py-3 text-sm font-medium text-green-900 dark:text-green-200 ${className}`}
      data-testid="knowledge-acquired"
    >
      {acquiredNotice(progress)}
    </div>
  );
}
